/**
 * Analytics › Content: every item watched in the range, as a table (name linking to the item's
 * page and its Analytics tab), sortable by what the owner cares about. Rates are worked out here
 * from the sums.
 */

import { useMemo, useState } from "react";
import FilterButtons from "@web-ui/components/FilterButtons";
import { SkeletonCard } from "@web-ui/components/Skeleton";
import { analytics, type ContentRow } from "../api";
import type { Table } from "../csv";
import { definition } from "../definitions";
import { count, duration, percent, rate } from "../format";
import { NoData, ReportFailed } from "../components/no-data";
import { ReportCard } from "../components/report-card";
import { SampleNotice } from "../components/sample-notice";
import type { ReportScope } from "../scope";
import { useReport } from "../use-report";

type Sort = "plays" | "watchSeconds" | "viewers";

export function Content({ scope }: { scope: ReportScope }) {
  const report = useReport(() => analytics.content(scope.client, scope.range), [scope.client, scope.range]);
  const [sort, setSort] = useState<Sort>("plays");
  const d = (id: Parameters<typeof definition>[0]) => definition(id, scope.pack);
  const rows = useMemo(() => [...(report.data?.items ?? [])].sort((a, b) => b[sort] - a[sort] || b.plays - a.plays), [report.data, sort]);

  if (report.failed && !report.data) return <section className="card"><ReportFailed /></section>;
  if (!report.data) return <SkeletonCard label="Loading what was watched" rows={6} />;

  const table: Table<ContentRow> = {
    columns: [
      { label: "Name", value: (r) => scope.nameOf(r.itemId).label, href: (r) => scope.nameOf(r.itemId).href },
      { label: "Item id", value: (r) => r.itemId, csvOnly: true },
      { label: d("plays").label, tooltip: d("plays").tooltip, value: (r) => r.plays, format: (v) => count(Number(v)), numeric: true },
      { label: d("engagedRate").label, tooltip: d("engagedRate").tooltip, csvLabel: "Engaged plays", value: (r) => r.engagedPlays, format: (_, r) => percent(rate(r.engagedPlays, r.plays)), numeric: true },
      { label: d("watchSeconds").label, tooltip: d("watchSeconds").tooltip, csvLabel: "Watch time (seconds)", value: (r) => r.watchSeconds, format: (v) => duration(Number(v)), numeric: true },
      { label: d("averageWatch").label, tooltip: d("averageWatch").tooltip, csvLabel: "Average watch (seconds)", value: (r) => (r.plays ? Math.round(r.watchSeconds / r.plays) : null), format: (v) => duration(v === null ? null : Number(v)), numeric: true },
      { label: d("viewers").label, tooltip: d("viewers").tooltip, value: (r) => r.viewers, format: (v) => count(Number(v)), numeric: true },
      { label: d("completionRate").label, tooltip: d("completionRate").tooltip, csvLabel: d("completed").label, value: (r) => r.completed, format: (_, r) => percent(rate(r.completed, r.plays)), numeric: true },
    ],
    rows,
  };
  const items = scope.pack.nouns.title.many.toLowerCase();

  return (
    <div className={`flex flex-col gap-3 transition-opacity ${report.stale ? "opacity-60" : ""}`} aria-busy={report.stale || undefined}>
      <SampleNotice shown={report.data.sample} />
      <ReportCard tableOnly title="What was watched" description={`Every ${scope.pack.nouns.title.one.toLowerCase()} and live event with plays in this period. Open one for how far people watch it.`}
        table={table} file={{ brand: scope.brand, what: "content", range: scope.range }}
        empty={rows.length === 0 ? <NoData why={`Numbers appear once viewers watch your ${items} in the apps.`} /> : undefined}
        actions={rows.length ? <FilterButtons label="Sort by" showLabel items={[{ id: "plays" as const, label: "Plays" }, { id: "watchSeconds" as const, label: "Watch time" }, { id: "viewers" as const, label: "Viewers" }]} value={sort} onChange={(next) => setSort(next as Sort)} /> : undefined} />
    </div>
  );
}
